/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
} from "@angular/core";
import { CommonModule, DatePipe, CurrencyPipe } from "@angular/common";

// PrimeNG
import {
  TableModule,
  TableLazyLoadEvent,
  TableRowExpandEvent,
  TableRowCollapseEvent,
} from "primeng/table";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import { MenuModule, Menu } from "primeng/menu";
import { MenuItem } from "primeng/api";
import { SkeletonModule } from "primeng/skeleton";
import { RippleModule } from "primeng/ripple";
import {
  TableSkeletonRowsComponent,
  SkeletonColumnConfig,
  SKELETON_PRESETS,
} from "../../../../shared/components/table-skeleton";

// Feature Interfaces & Constants
import {
  CouponListResponse,
  CouponRowAction,
  CouponRowActionEvent,
  CouponSortEvent,
  CouponPageEvent,
} from "../../interfaces";
import { QPilotCoupon } from "../../../../core/models/qpilot/coupon.interface";
import {
  getDiscountTypeLabel,
  formatDiscountAmount,
  PAGE_SIZE_OPTIONS,
  DEFAULT_PAGE_SIZE,
} from "../../constants/coupon.constants";

/**
 * Coupons Table Component
 * Data table with sorting, pagination, row expansion, and row actions
 * Based on customers-table pattern with proper mobile responsive layout
 */
@Component({
  selector: "app-coupons-table",
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    MenuModule,
    SkeletonModule,
    RippleModule,
    DatePipe,
    CurrencyPipe,
    TableSkeletonRowsComponent,
  ],
  templateUrl: "./coupons-table.component.html",
  styleUrls: ["./coupons-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouponsTableComponent {
  @Input() data: CouponListResponse | null = null;
  @Input() loading = false;
  @Input() scrollHeight = "flex";

  @Output() sortChanged = new EventEmitter<CouponSortEvent>();
  @Output() pageChanged = new EventEmitter<CouponPageEvent>();
  @Output() rowAction = new EventEmitter<CouponRowActionEvent>();

  // Constants
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly defaultPageSize = DEFAULT_PAGE_SIZE;
  readonly skeletonColumns: SkeletonColumnConfig[] = SKELETON_PRESETS.coupons;

  // Row expansion tracking
  expandedRows: { [key: number]: boolean } = {};

  // Menu reference for programmatic toggle
  @ViewChild("actionsMenu") actionsMenu!: Menu;

  // Menu items for actions
  menuItems: MenuItem[] = [];
  private selectedCoupon: QPilotCoupon | null = null;

  // Getter for table data
  get tableData(): QPilotCoupon[] {
    return this.data?.Items ?? [];
  }

  // Getter for first record (pagination offset)
  get firstRecord(): number {
    if (!this.data?.Page || !this.data?.PageSize) return 0;
    return (this.data.Page - 1) * this.data.PageSize;
  }

  /**
   * Handle lazy load event (sort + pagination)
   */
  onLazyLoad(event: TableLazyLoadEvent): void {
    // Handle sort
    if (event.sortField) {
      this.sortChanged.emit({
        field: event.sortField as string,
        order: event.sortOrder as 1 | -1,
      });
    }

    // Handle pagination
    const first = event.first ?? 0;
    const rows = event.rows ?? this.defaultPageSize;
    const page = Math.floor(first / rows);

    this.pageChanged.emit({
      first,
      rows,
      page,
    });
  }

  /**
   * Toggle row expansion programmatically
   */
  toggleRowExpansion(coupon: QPilotCoupon): void {
    const id = coupon.Id;
    if (this.expandedRows[id]) {
      delete this.expandedRows[id];
    } else {
      // Collapse all other rows (single expansion mode)
      this.expandedRows = { [id]: true };
    }
  }

  /**
   * Handle row expand event
   */
  onRowExpand(event: TableRowExpandEvent): void {
    // Single expansion mode - collapse others
    this.expandedRows = { [event.data.Id]: true };
  }

  /**
   * Handle row collapse event
   */
  onRowCollapse(event: TableRowCollapseEvent): void {
    delete this.expandedRows[event.data.Id];
  }

  /**
   * Show actions menu for a coupon
   */
  showActionsMenu(event: Event, coupon: QPilotCoupon): void {
    this.selectedCoupon = coupon;
    this.menuItems = [
      {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () => this.emitAction("edit"),
      },
      { separator: true },
      {
        label: "Delete",
        icon: "pi pi-trash",
        styleClass: "text-red-500",
        command: () => this.emitAction("delete"),
      },
    ];

    // Toggle menu using ViewChild reference
    this.actionsMenu.toggle(event);
  }

  /**
   * Handle ID click - navigate to edit page
   */
  onIdClick(coupon: QPilotCoupon): void {
    this.rowAction.emit({ action: "edit", coupon });
  }

  /**
   * Emit row action event
   */
  private emitAction(action: CouponRowAction): void {
    if (this.selectedCoupon) {
      this.rowAction.emit({ action, coupon: this.selectedCoupon });
    }
  }

  /**
   * Get display label for discount type
   */
  getDiscountLabel(discountType: string): string {
    return getDiscountTypeLabel(discountType);
  }

  /**
   * Format discount amount based on type
   */
  formatAmount(discountType: string, amount: number): string {
    return formatDiscountAmount(discountType, amount);
  }

  /**
   * Get severity color for discount type tag
   */
  getDiscountTypeSeverity(
    discountType: string,
  ): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" {
    if (discountType.includes("Subtotal")) {
      return "success";
    }
    if (discountType.includes("Shipping")) {
      return "info";
    }
    return "secondary";
  }

  /**
   * Check if coupon is expired
   */
  isExpired(expirationDate: string | Date): boolean {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    return expDate < new Date();
  }

  /**
   * Format date for display
   */
  formatDate(date: string | Date | undefined): string {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}
